import { Box, Card, Dialog, Grid, Stack, Tab, Tabs, CircularProgress, Typography } from "@mui/material";
import React, { useEffect, useState, useMemo } from "react";
import ByzMuiButton from "src/components/core-components/mui/buttons/ByzMuiButton";
import ByzDataTreeListCarePlan from "src/components/core-components/mui/data-grid/ByzDataTreeListCarePlan";
import ByzMuiTypography from "src/components/core-components/mui/inputs/ByzMuiTypography";
import { TreeDataPlanGuide } from "../data";
import { GridToolbar, GridToolbarQuickFilter } from "@mui/x-data-grid-premium";
import AddIcon from "@mui/icons-material/Add";
import {
  GridRenderCellParams,
  GridToolbarContainer,
  GridColDef,
} from "@mui/x-data-grid";
import { useTranslate } from "src/locales";
import ByzToggleIconButton from "src/components/core-components/mui/buttons/ByzToggleIconButton.tsx";
import { ByzMuiDatePicker } from "src/components/core-components/mui/inputs/ByzMuiDatePicker";
import PageOne from "../patient-care-history/page-one";
import ByzEditButton from "src/components/core-components/mui/buttons-grid/ByzGrdEditButton";
import { useBaseQuery } from "src/api/core/useBaseQuery";
import PatientCareNursePlanAdd from "../modal/patient-care-nurse-plan-add";
import "./disabled-row.css";
import { CreatePatientNurseCarePlanRequestDto } from "src/api/nurse/nurseServiceDtos";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { nurseService } from "src/api/nurse/nurseService";
import { useSnackbar } from "notistack";
import { useAppSelector } from "src/app/hooks";
import { KeyboardArrowRight } from "@mui/icons-material";
import ByzMuiCloseButton from "src/components/core-components/mui/buttons/ByzMuiCloseButton";
import ByzMuiAddButton from "src/components/core-components/mui/buttons/ByzMuiAddButton";
import { formatLongDate } from "src/utils/date";
import ByzDataGridToolbar from "src/components/core-components/mui/data-grid/toolbar";

// Common grid header style used for standardization
const commonGridHeaderStyle = {
  "& .MuiDataGrid-columnHeaders": {
    backgroundColor: "#F4F7FE !important",
    minHeight: "48px !important",
  },
  "& .MuiDataGrid-columnHeader": {
    backgroundColor: "#F4F7FE !important",
    borderRight: "1px solid #E0E0E0",
    borderBottom: "1px solid #E0E0E0",
  },
  "& .MuiDataGrid-columnHeaderTitle": {
    fontFamily: "Poppins, sans-serif !important",
    fontWeight: "700 !important",
    fontSize: "12px !important",
    lineHeight: "24px !important",
    letterSpacing: "0.17px !important",
    color: "#000000 !important",
    textAlign: "center",
    justifyContent: "center",
    display: "flex",
    width: "100%",
  },
  "& .MuiDataGrid-cell": {
    borderRight: "1px solid #E0E0E0",
    borderBottom: "1px solid #E0E0E0",
  },
};
const mapNurseCaresToTreeDataPlanGuide = (nurseCares: any[]): TreeDataPlanGuide[] => {
  return nurseCares.map(item => {
    const name = item.ncpname?.trim() || item.name?.trim() || "";
    return {
      id: Number(item.keyid),
      parentId: item.parentid !== null ? Number(item.parentid) : null,
      name: name,
      displayName: name,
    };
  });
};

const flattenTree = (data: TreeDataPlanGuide[], parentId: number | null = null): TreeDataPlanGuide[] => {
  const result: TreeDataPlanGuide[] = [];
  data
    .filter(item => item.parentId === parentId)
    .forEach(parent => {
      result.push(parent);
      result.push(...flattenTree(data, parent.id));
    });
  return result;
};

function PatientCareGuide({ patientId, patientInfo, onClose }: { patientId?: number; patientInfo?: any; onClose?: () => void }) {

  const [isPlanEditDialogOpen, setPlanEditDialogOpen] = useState(false);
  const [PlancurrentEditRow, setPlanCurrentEditRow] = useState<any>(null);
  const [activeLeftTab, setActiveLeftTab] = useState(0);
  const { t } = useTranslate();
  const [selectedPlanRows, setSelectedPlanRows] = useState<any[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [selectedNurseRows, setSelectedNurseRows] = useState<any[]>([]);
  const [selectedNurseTreeIds, setSelectedNurseTreeIds] = useState<number[]>([]);
  const [addedNurseRowIds, setAddedNurseRowIds] = useState<number[]>([]);
  const [selectedBottomRowIds, setSelectedBottomRowIds] = useState<number[]>([]);
  const [isAdding, setIsAdding] = useState(false);
  const [favoriteIds, setFavoriteIds] = useState<number[]>([]);
  const [favoriteIdMap, setFavoriteIdMap] = useState<Map<number, number>>(new Map());
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAppSelector(state => state.auth);
  const queryClient = useQueryClient();

  const { data: favoriteGroups } = useBaseQuery<{ data: any[] }>({
    queryKey: ["favoriteNurseCareGroups", user?.id],
    endpoint: user?.id ? `/nurse/api/FavoriteNurseCareGroup/getFavoriteNurseCareGroupsByUserId/${user.id}` : "",
    options: {
      refetchOnWindowFocus: false,
    },
  });

  const { data: favoritePlans } = useBaseQuery<{ data: any[] }>({
    queryKey: ["favoriteNurseCarePlans", user?.id],
    endpoint: user?.id ? `/nurse/api/FavoriteNurseCarePlan/getFavoriteNurseCarePlansByUserId/${user.id}` : "",
    options: {
      refetchOnWindowFocus: false,
    },
  });

  const createFavoriteNurseCareGroupMutation = useMutation({
    mutationFn: (data: { ncgId: number; userId: number }) =>
      nurseService.favoriteNurseCareGroup.createFavoriteNurseCareGroup(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["favoriteNurseCareGroups", user?.id] });
    },
  });

  const deleteFavoriteNurseCareGroupMutation = useMutation({
    mutationFn: (id: number) =>
      nurseService.favoriteNurseCareGroup.deleteFavoriteNurseCareGroup({ id }),
    onSuccess: () => {
      // Query'yi invalidate et
      queryClient.invalidateQueries({ queryKey: ["favoriteNurseCareGroups", user?.id] });
    },
  });

  const createFavoriteNurseCarePlanMutation = useMutation({
    mutationFn: (data: { ncgpId: number; userId: number }) =>
      nurseService.favoriteNurseCarePlan.createFavoriteNurseCarePlan(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["favoriteNurseCarePlans", user?.id] });
    },
  });

  const deleteFavoriteNurseCarePlanMutation = useMutation({
    mutationFn: (id: number) =>
      nurseService.favoriteNurseCarePlan.deleteFavoriteNurseCarePlan({ id }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["favoriteNurseCarePlans", user?.id] });
    },
  });

  const { data: nurseCares, isLoading: isLoadingNurseCares } = useBaseQuery<{ data: any[] }>({
    queryKey: ["nurseCares"],
    endpoint: "nurse/api/NurseCare/getAllNurseCares",
    options: {
      refetchOnWindowFocus: false,
    },
  });

  useEffect(() => {
    if (favoriteGroups?.data && favoritePlans?.data && nurseCares?.data) {
      const newFavoriteIdMap = new Map<number, number>();


      const groupIds = favoriteGroups.data.map((item: any) => {
        const ncgId = item.ncgid || item.ncgId;
        const favoriteId = item.id;
        const nurseCareItem = nurseCares.data.find((nurseItem: any) =>
          !nurseItem.ncpid && Number(nurseItem.ncgid) === Number(ncgId)
        );
        if (nurseCareItem) {
          const keyid = Number(nurseCareItem.keyid);
          newFavoriteIdMap.set(keyid, favoriteId);
          return keyid;
        }
        return null;
      }).filter((id): id is number => id !== null);

      const planIds = favoritePlans.data.map((item: any) => {
        const ncpId = item.ncgpid || item.ncpId;
        const favoriteId = item.id;
        const nurseCareItem = nurseCares.data.find((nurseItem: any) =>
          Number(nurseItem.ncpid) === Number(ncpId)
        );
        if (nurseCareItem) {
          const keyid = Number(nurseCareItem.keyid);
          newFavoriteIdMap.set(keyid, favoriteId);
          return keyid;
        }
        return null;
      }).filter((id): id is number => id !== null);

      setFavoriteIds([...groupIds, ...planIds]);
      setFavoriteIdMap(newFavoriteIdMap);
    }
  }, [favoriteGroups?.data, favoritePlans?.data, nurseCares?.data]);

  // Memoize tree data to prevent unnecessary re-calculations
  const treeDataPlanGuide: TreeDataPlanGuide[] = useMemo(() => {
    return Array.isArray(nurseCares?.data)
      ? mapNurseCaresToTreeDataPlanGuide(nurseCares!.data)
      : [];
  }, [nurseCares]);

  const sortedTreeDataPlanGuide = useMemo(() => flattenTree(treeDataPlanGuide), [treeDataPlanGuide]);

  // Favorilere göre tree data'yı filtrele
  const filteredTreeData = useMemo(() => {
    if (activeLeftTab === 1) { // Favoriler sekmesi
      if (favoriteIds.length === 0) {
        return []; // Favori yoksa boş array döndür
      }

      const filteredData: TreeDataPlanGuide[] = [];

      favoriteIds.forEach(favId => {
        const favoriteItem = treeDataPlanGuide.find(item => item.id === favId);

        if (!favoriteItem) return;

        if (!favoriteItem.parentId) {
          filteredData.push(favoriteItem);
          const children = treeDataPlanGuide.filter(item => item.parentId === favId);
          filteredData.push(...children);
        } else {
          const parent = treeDataPlanGuide.find(item => item.id === favoriteItem.parentId);
          if (parent) {
            filteredData.push(parent);
          }
          filteredData.push(favoriteItem);
        }
      });

      // Duplicate'ları kaldır
      const uniqueData = filteredData.filter((item, index, self) =>
        index === self.findIndex(t => t.id === item.id)
      );

      console.log('filteredTreeData Result:', uniqueData);

      const sortedData: TreeDataPlanGuide[] = [];

      const parents = uniqueData.filter(item => !item.parentId);
      parents.forEach(parent => {
        sortedData.push(parent);
        // Bu parent'ın child'larını da ekle
        uniqueData.filter(item => item.parentId === parent.id).forEach(child => {
          sortedData.push(child);
        });
      });

      return sortedData;
    }

    return sortedTreeDataPlanGuide; // All sekmesi için tüm veriler (flattened)
  }, [activeLeftTab, favoriteIds, treeDataPlanGuide, sortedTreeDataPlanGuide]);
  const selectedChild = sortedTreeDataPlanGuide.find(item => item.id === selectedId);
  const parent = sortedTreeDataPlanGuide.find(item => item.id === selectedChild?.parentId);

  const nurseCareObj = nurseCares?.data?.find(item => Number(item.keyid) === selectedId);
  const ncpId = nurseCareObj?.ncpid;

  const { data: carePlanDetails } = useBaseQuery<{ data: any[] }>({
    queryKey: ["nurseCarePlanDetails", ncpId],
    endpoint: ncpId ? `/nurse/api/NurseCare/getAllNurseCarePlanDetailsByNcpId?ncpId=${ncpId}&isActive=1` : "",
    options: {
      refetchOnWindowFocus: false,
    },
  });

  const nurseRows = useMemo(() => {
    return Array.isArray(carePlanDetails?.data)
      ? carePlanDetails?.data?.map(item => ({
        id: item.id,
        guideName: parent?.name || "",
        planName: selectedChild?.name || "",
        nursingInitiative: item.name,
        period: item.period,
        ncpId: ncpId, // burada ekle!
      }))
      : [];
  }, [carePlanDetails?.data, parent?.name, selectedChild?.name, ncpId]);

  const columnsPlan: GridColDef[] = [
    {
      field: "nursingInitiative",
      headerName: t("patient-care-guide.attempt"),
      flex: 1,
      headerClassName: "header-theme",
    },
    {
      field: "period",
      headerName: t("patient-care-guide.period"),
      width: 200,
      headerClassName: "header-theme",
      renderCell: (params) => {
        const val = parseFloat(params.value);
        return (params.value && !isNaN(val)) ? (val / 60) + " " + t("hour") : "";
      }
    },
    {
      field: "startDate",
      headerName: t("patient-care.first-day"),
      width: 200,
      headerClassName: "header-theme",
      renderCell: (params) => (
        formatLongDate(params.value)
      ),
    },
    {
      field: "endDate",
      headerName: t("patient-care.last-day"),
      width: 200,
      headerClassName: "header-theme",
      renderCell: (params) => (
        formatLongDate(params.value)
      ),
    },
    {
      field: "edit",
      headerName: t("patient-care-guide.columns.edit"),
      width: 100,
      headerClassName: "header-theme",
    },
  ];

  const updatedColumnsPlan = columnsPlan.map((columnsPlan) => ({
    ...columnsPlan,
    renderCell:
      columnsPlan.field === "edit"
        ? (params: GridRenderCellParams) => (
          <Grid>
            {params.row.id !== null && !params.row.isMaster && (
              <ByzEditButton
                color="info"
                onClick={() => handleOpenDialogPlanEdit(params.row)}
                style={{ cursor: "pointer" }}
              />
            )}
          </Grid>
        )
        : columnsPlan.renderCell,
  }));

  const createPatientNurseCarePlanMutation = useMutation({
    mutationFn: (data: CreatePatientNurseCarePlanRequestDto) =>
      nurseService.patientNurseCarePlan.createPatientNurseCarePlan(data),
  });

  const handleAddPatient = async () => {
    if (selectedBottomRowIds.length === 0) {
      enqueueSnackbar("Seçili plan veya detay yok", { variant: 'warning' });
      return;
    }

    setIsAdding(true);
    enqueueSnackbar("Hastaya ekleniyor, lütfen bekleyin...", { variant: 'info' });

    try {
      const selectedRows = selectedPlanRows.filter(row => selectedBottomRowIds.includes(row.id));
      console.log(selectedRows);
      const requestData: CreatePatientNurseCarePlanRequestDto = {
        patientid: patientId,
        nursecareplan: selectedRows.map(row => ({
          nursecareplanid: row.ncpId,
          nursecareplandetailid: row.ncpd_id || row.id,
          nursecareplandetailperiod: row.period,
          nursecareplanstartdate: row.startDate ? new Date(row.startDate).toISOString() : "",
          nursecareplanenddate: row.endDate ? new Date(row.endDate).toISOString() : "",
        }))
      };

      await createPatientNurseCarePlanMutation.mutateAsync(requestData);

      // Geçmiş bakım planı listesini yenile
      queryClient.invalidateQueries({ queryKey: ["history", patientId] });

      setSelectedPlanRows(prev => prev.filter(row => !selectedBottomRowIds.includes(row.id)));
      setAddedNurseRowIds(prev => prev.filter(id => !selectedBottomRowIds.includes(id)));
      setSelectedBottomRowIds([]); // Seçimi temizle
      enqueueSnackbar('Hastaya bakım planı eklendi.', { variant: 'success' });
    } catch (error) {
      console.error("Hata:", error);
      enqueueSnackbar('Ekleme sırasında hata oluştu.', { variant: 'error' });
    } finally {
      setIsAdding(false);
    }
  };

  const handleOpenDialogPlanEdit = (row: any) => {
    setPlanCurrentEditRow(row.id); // Sadece id gönder
    setPlanEditDialogOpen(true);
  };

  const handleCloseDialogPlanEdit = () => {
    setPlanEditDialogOpen(false);
    setPlanCurrentEditRow(null);
  };

  const [startDate, setStartDate] = useState<Date | null>(new Date());
  const [endDate, setEndDate] = useState<Date | null>(new Date());

  const handleStartDateChange = (date: Date | null) => {
    setStartDate(date);
    if (date && endDate && date > endDate) {
      setEndDate(date);
    }
  };

  const [isEyeOpen, setIsEyeOpen] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isArrowUp, setIsArrowUp] = useState(false);



  const CustomToolbarInline = ({ searchInput, onSearchInputChange }: { searchInput: string, onSearchInputChange: (val: string) => void }) => {
    const [localInput, setLocalInput] = useState(searchInput || "");

    useEffect(() => {
      setLocalInput(searchInput);
    }, [searchInput]);

    useEffect(() => {
      const timer = setTimeout(() => {
        if (onSearchInputChange) onSearchInputChange(localInput);
      }, 500);
      return () => clearTimeout(timer);
    }, [localInput, onSearchInputChange]);

    return (
      <GridToolbarContainer sx={{ color: "#00B8D9", display: "flex", alignItems: "center", width: "100%" }}>
        <GridToolbar />
        <GridToolbarQuickFilter
          placeholder={t("search")}
          sx={{ ml: "auto", minWidth: 300 }}
          value={localInput || ""}
          onChange={(e) => setLocalInput(e.target.value)}
        />
      </GridToolbarContainer>
    );
  };

  const CustomToolbarSearch = ({ searchInput, onSearchInputChange, onSearch }: {
    searchInput: string;
    onSearchInputChange: (value: string) => void;
    onSearch: () => void;
  }) => {
    const [localInput, setLocalInput] = useState(searchInput);

    // searchInput prop'u değiştiğinde localInput'u güncelle
    useEffect(() => {
      setLocalInput(searchInput);
    }, [searchInput]);

    // Yazdıkça arama için debounce
    useEffect(() => {
      const timer = setTimeout(() => {
        onSearchInputChange(localInput);
      }, 500); // 500ms bekle

      return () => clearTimeout(timer);
    }, [localInput, onSearchInputChange]);

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      setLocalInput(event.target.value);
    };

    return (
      <Box sx={{ width: "100%", p: 1 }}>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sx={{ borderBottom: "1px solid #f0f0f0", pb: 1, mb: 1 }}>
            <ByzMuiTypography isTitle={true} sx={{ ml: 1 }}>
              {t("patient-care-guide.care-planing")}
            </ByzMuiTypography>
          </Grid>

          <Grid item xs={12}>
            <Grid container spacing={1} sx={{ px: 1 }}>
              <Grid item xs={4}>
                <ByzMuiTypography variant="body2" color="text.secondary" sx={{ fontSize: 13, fontWeight: 500 }}>
                  {t("patient-care-guide.name-surname")}:
                </ByzMuiTypography>
              </Grid>
              <Grid item xs={8}>
                <ByzMuiTypography variant="body2" sx={{ fontWeight: "bold", fontSize: 13 }}>
                  {patientInfo?.name} {patientInfo?.surname}
                </ByzMuiTypography>
              </Grid>

              <Grid item xs={4}>
                <ByzMuiTypography variant="body2" color="text.secondary" sx={{ fontSize: 13, fontWeight: 500 }}>
                  {t("patient-care-guide.file-prot")}:
                </ByzMuiTypography>
              </Grid>
              <Grid item xs={8}>
                <ByzMuiTypography variant="body2" sx={{ fontWeight: "bold", fontSize: 13 }}>
                  {patientInfo?.upn} / {patientInfo?.protid}
                </ByzMuiTypography>
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        <Box sx={{ width: "100%", px: 1, mb: 2 }}>
          <GridToolbarQuickFilter
            placeholder={t("search")}
            sx={{
              width: "100%",
              "& .MuiInputBase-root": { width: "100%" },
              "& .MuiSvgIcon-root": { color: "#00B8D9" }
            }}
            value={localInput}
            onChange={handleChange}
          />
        </Box>
        <Tabs
          orientation="vertical"
          value={activeLeftTab}
          onChange={(_, v) => setActiveLeftTab(v)}
          TabIndicatorProps={{ style: { display: "none" } }}
          sx={{ ".MuiTabs-flexContainer": { alignItems: "stretch", } }}
        >
          {[
            t("patient-diet-procedures.add-popup.history"),
            t("patient-diet-procedures.add-popup.favorites"),
            t("patient-diet-procedures.add-popup.all"),
          ].map((label, idx) => (
            <Tab
              key={idx}
              label={label}
              value={idx}
              icon={<KeyboardArrowRight fontSize="small" />}
              iconPosition="start"
              sx={{
                minWidth: "100%",
                textTransform: "none",
                justifyContent: "flex-start",
                borderBottom: "1px solid #E0E0E0",
                bgcolor: activeLeftTab === idx ? "#F4F7FE" : "transparent",
                pl: 2
              }}
            />
          ))}
        </Tabs>
      </Box>
    );
  };

  const handleTimeSelected = (time: number, pcsid: number | null) => {
    if (pcsid === null) return;
    setSelectedNurseRows(prev =>
      prev.map(row =>
        row.id === pcsid ? { ...row, period: time * 60 } : row
      )
    );
    setSelectedPlanRows(prev =>
      prev.map(row =>
        row.id === pcsid ? { ...row, period: time * 60 } : row
      )
    );
  };

  // Favori işlemleri
  const handleFavoriteToggle = async (id: number, isFavorite: boolean) => {
    if (!user?.id) {
      enqueueSnackbar('Kullanıcı bilgisi bulunamadı', { variant: 'error' });
      return;
    }

    try {
      // Seçilen item'ın parent mı child mı olduğunu kontrol et
      const selectedItem = sortedTreeDataPlanGuide.find(item => item.id === id);

      const isParent = selectedItem && !selectedItem.parentId; // parentId null ise parent'tır

      if (isFavorite) {
        // Favorilere ekle
        if (isParent) {
          // Parent (Nurse Care Group) - ncgId kullan
          const nurseCareObj = nurseCares?.data?.find(item => Number(item.keyid) === id);
          const actualNcgId = nurseCareObj?.ncgid;

          if (!actualNcgId) {
            enqueueSnackbar('Nurse Care Group ID bulunamadı', { variant: 'error' });
            return;
          }

          await createFavoriteNurseCareGroupMutation.mutateAsync({
            ncgId: actualNcgId,
            userId: user.id
          });
        } else {
          // Child (Nurse Care Plan) - ncpId kullan
          // nurseCares'den seçilen satırı bul ve gerçek ncpId'yi al
          const nurseCareObj = nurseCares?.data?.find(item => Number(item.keyid) === id);
          const actualNcpId = nurseCareObj?.ncpid;

          if (!actualNcpId) {
            enqueueSnackbar('Nurse Care Plan ID bulunamadı', { variant: 'error' });
            return;
          }

          await createFavoriteNurseCarePlanMutation.mutateAsync({
            ncgpId: actualNcpId,
            userId: user.id
          });
        }
        setFavoriteIds(prev => [...prev, id]);
        enqueueSnackbar('Favorilere eklendi', { variant: 'success' });
      } else {
        // Favorilerden çıkar
        if (isParent) {
          // Parent (Nurse Care Group) - favori kaydının kendi id'sini kullan
          const favoriteId = favoriteIdMap.get(id);
          if (!favoriteId) {
            enqueueSnackbar('Favori kaydı bulunamadı', { variant: 'error' });
            return;
          }
          await deleteFavoriteNurseCareGroupMutation.mutateAsync(favoriteId);
        } else {
          // Child (Nurse Care Plan) - favori kaydının kendi id'sini kullan
          const favoriteId = favoriteIdMap.get(id);
          if (!favoriteId) {
            enqueueSnackbar('Favori kaydı bulunamadı', { variant: 'error' });
            return;
          }
          await deleteFavoriteNurseCarePlanMutation.mutateAsync(favoriteId);
        }
        setFavoriteIds(prev => prev.filter(favId => favId !== id));
        enqueueSnackbar('Favorilerden çıkarıldı', { variant: 'info' });
      }
    } catch (error) {
      console.error('Favori işlemi hatası:', error);
      enqueueSnackbar('Favori işlemi sırasında hata oluştu', { variant: 'error' });
    }
  };

  /* Nurse Tree Data oluşturma - Guide -> Plan -> Detail hiyerarşisi */
  const nurseTreeData = useMemo(() => {
    const data: any[] = [];
    const PARENT_ID_PREFIX = 100000000; // Çakışmayı önlemek için büyük offset

    if (parent && selectedChild) {
      const combinedId = selectedChild.id + PARENT_ID_PREFIX;
      data.push({
        id: combinedId,
        parentId: null,
        name: <span>
          <span><b>{t("patient-care-guide.guide-name")} :</b> <span style={{
            fontWeight: 275, fontStyle: "Italic",
            fontSize: "14px", lineHeight: "24px", letterSpacing: "0.17px"
          }}>{parent.name}</span></span>
          {" | "}
          <span><b>{t("patient-care-guide.plan")} :</b> <span style={{
            fontWeight: 275, fontStyle: "Italic",
            fontSize: "14px", lineHeight: "24px", letterSpacing: "0.17px"
          }}>{selectedChild.name}</span></span>
        </span>,
        displayName: (
          <span>
            <span><b>{t("patient-care-guide.guide-name")} :</b> <span style={{
              fontWeight: 275, fontStyle: "Italic",
              fontSize: "14px", lineHeight: "24px", letterSpacing: "0.17px"
            }}>{parent.name}</span></span>
            {" | "}
            <span><b>{t("patient-care-guide.plan")} :</b> <span style={{
              fontWeight: 275, fontStyle: "Italic",
              fontSize: "14px", lineHeight: "24px", letterSpacing: "0.17px"
            }}>{selectedChild.name}</span></span>
          </span>
        ),
        isMaster: true
      });

      if (nurseRows) {
        nurseRows.forEach((row) => {
          data.push({
            ...row,
            parentId: combinedId,
            name: row.nursingInitiative,
            displayName: row.nursingInitiative,
          });
        });
      }
    } else if (parent) {
      data.push({
        id: parent.id + PARENT_ID_PREFIX,
        parentId: null,
        name: `${t("patient-care-guide.guide-name")} : ${parent.name}`,
        displayName: (
          <span>
            <b>{t("patient-care-guide.guide-name")} :</b> <span style={{
              fontWeight: 275, fontStyle: "Italic",
              fontSize: "14px", lineHeight: "24px", letterSpacing: "0.17px"
            }}>{parent.name}</span>
          </span>
        ),
        isMaster: true
      });

      if (nurseRows) {
        nurseRows.forEach((row) => {
          data.push({
            ...row,
            parentId: parent.id + PARENT_ID_PREFIX,
            name: row.nursingInitiative,
            displayName: row.nursingInitiative,
          });
        });
      }
    }
    return data;
  }, [parent, selectedChild, nurseRows, t]);

  // Basit string hash fonksiyonu
  const hashCode = (str: string) => {
    if (!str) return 0; // Handle undefined/null/empty
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    return Math.abs(hash);
  };

  /* Selected Plan Tree Data oluşturma - Bottom Grid */
  const selectedPlanTreeData = useMemo(() => {
    const data: any[] = [];
    const PLAN_PREFIX = 300000000;
    const planMap = new Set<string>();

    if (selectedPlanRows) {
      selectedPlanRows.forEach(row => {
        const guideName = row.guideName || row.guideTitle || "";
        const planName = row.planName || "";
        const detailName = row.nursingInitiative || row.name || "";

        let combinedName = "";
        if (guideName) {
          combinedName += `${t("patient-care-guide.guide-name")} : ${guideName}`;
        }
        if (guideName && planName) {
          combinedName += " | ";
        }
        if (planName) {
          combinedName += `${t("patient-care-guide.plan")} : ${planName}`;
        }

        const combinedNameJSX = (
          <span>
            {guideName && <span><b>{t("patient-care-guide.guide-name")} :</b> <span style={{
              fontWeight: 275, fontStyle: "Italic",
              fontSize: "14px", lineHeight: "24px", letterSpacing: "0.17px"
            }}>{guideName}</span></span>}
            {guideName && planName && " | "}
            {planName && <span><b>{t("patient-care-guide.plan")} :</b> <span style={{
              fontWeight: 275, fontStyle: "Italic",
              fontSize: "14px", lineHeight: "24px", letterSpacing: "0.17px"
            }}>{planName}</span></span>}
          </span>
        );
        const planKey = guideName + "___" + planName;

        if (!planMap.has(planKey)) {
          planMap.add(planKey);
          const planId = PLAN_PREFIX + hashCode(planKey);
          data.push({
            id: planId,
            parentId: null,
            nursingInitiative: combinedNameJSX,
            name: combinedName,
            isMaster: true,
            period: null
          });
        }
        const planId = PLAN_PREFIX + hashCode(planKey);

        // Detail Row
        data.push({
          ...row,
          parentId: planId,
          nursingInitiative: detailName, // Ensure tree column field is populated
          name: detailName
        });
      });
    }
    return data;
  }, [selectedPlanRows, t]);

  return (
    <Box sx={{ p: 2, width: "100%", height: "100%", position: "relative" }}>
      <Grid container spacing={1} sx={{ height: "100%" }}>
        <Grid item xs={12} md={4} sx={{ height: "700px" }}>
          {isLoadingNurseCares ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <CircularProgress />
              <Typography sx={{ ml: 2 }}>{t("loading")}</Typography>
            </Box>
          ) : (
            <ByzDataTreeListCarePlan
              initialData={filteredTreeData}
              columns={[{ field: "displayName", headerName: t("patient-care-guide.columns.name"), flex: 1 }]}
              customToolbar={CustomToolbarSearch}
              onSelectionChange={(ids) => {
                setSelectedId(Array.isArray(ids) ? ids[0] : ids);
                setSelectedNurseTreeIds([]);
                setSelectedNurseRows([]);
              }}
              hideColumns
              showFavoriteInMenu={true}
              onFavoriteToggle={handleFavoriteToggle}
              favoriteIds={favoriteIds}
              showDefaultMenuItems={false}
              whiteGrid={true}
              actionColumnPosition="end"
              treeColumn="displayName"
              showFooter={false}
              isActionColumnShow={true}
              sx={commonGridHeaderStyle}
            />
          )}
        </Grid>
        <Grid
          item
          xs={12}
          md={8}
          sx={{ borderLeft: "1px solid #E0E0E0", height: "700px", display: "flex", flexDirection: "column" }}
        >
          {activeLeftTab === 0 ? (
            <PageOne
              key={patientId}
              ptId={patientId}
              onAddSelectedRows={(rows) => {
                setSelectedPlanRows(prev => [...prev, ...rows]);
                setSelectedBottomRowIds(prev => [...prev, ...rows.map((row: any) => row.id)]);
              }}
              onClose={onClose}
            />
          ) : (
            // Favorites ve All sekmesi - aynı ekran kalır
            <Box sx={{ width: "100%", height: "100%" }}>
              <Grid
                container
                sx={{
                  borderBottom: "1px solid #E0E0E0",
                }}
              >
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ width: "100%", mb: 3 }}
                >
                  <ByzMuiTypography isTitle={true} sx={{ ml: 0 }}>
                    {t("patient-care-guide.add-patient-care-plan")}
                  </ByzMuiTypography>

                  <ByzMuiCloseButton onClick={onClose || (() => { })} />
                </Stack>
                <Stack direction="row" spacing={3} sx={{ width: "100%", pb: 2 }}>
                  <ByzMuiDatePicker
                    label={t("patient-care.first-day")}
                    value={startDate}
                    onChange={handleStartDateChange}
                    minDate={new Date()}
                  />
                  <ByzMuiDatePicker
                    label={t("patient-care.last-day")}
                    value={endDate}
                    onChange={setEndDate}
                    minDate={startDate ?? undefined}
                  />
                  <ByzMuiAddButton
                    onClick={() => {
                      const newRows = selectedNurseRows.map(row => ({
                        ...row,
                        startDate: startDate ? new Date(startDate).toISOString().slice(0, 10) : "",
                        endDate: endDate ? new Date(endDate).toISOString().slice(0, 10) : "",
                        isPlan: false,
                      }));
                      setSelectedPlanRows(prev => [...prev, ...newRows]);
                      setSelectedBottomRowIds(prev => [...prev, ...newRows.map(row => row.id)]);
                      setAddedNurseRowIds(prev => [
                        ...prev,
                        ...selectedNurseRows.map(row => row.id)
                      ]);
                      setSelectedNurseRows([]); // Seçimi temizle
                      setSelectedNurseTreeIds([]);
                    }}
                    sx={{ height: 40, alignSelf: "center" }}
                  />
                </Stack>
                <Box component="span" sx={{ fontWeight: "bold", fontSize: "1.2rem" }}>
                  {t("patient-care-guide.nursing-initiatives")}
                </Box>
              </Grid>
              <ByzDataTreeListCarePlan
                customToolbar={ByzDataGridToolbar}
                columns={[
                  {
                    field: "name",
                    headerName: t("patient-care-guide.nursing-initiatives"),
                    flex: 1,
                  },
                  {
                    field: "period",
                    headerName: t("patient-care-guide.period"),
                    width: 200,
                    renderCell: (params: any) => {
                      const val = parseFloat(params.value);
                      return (params.value && !isNaN(val)) ? (val / 60) + " " + t("hour") : "";
                    }
                  }
                ]}
                initialData={nurseTreeData}
                hideColumns
                checkboxSelection
                sx={{ flex: 1, minHeight: 0, ...commonGridHeaderStyle }}
                selectedIds={selectedNurseTreeIds}
                onSelectionChange={(ids: any) => {
                  const currentSelectedIds = (Array.isArray(ids) ? ids : [ids]).map(Number);
                  const prevSelectedIds = selectedNurseTreeIds;

                  // Find what was just changed
                  const newlySelected = currentSelectedIds.filter(id => !prevSelectedIds.includes(id));
                  const newlyDeselected = prevSelectedIds.filter(id => !currentSelectedIds.includes(id));

                  let nextSelectedIds = [...currentSelectedIds];

                  // If a parent (master) was selected, select all its children
                  newlySelected.forEach(id => {
                    const row = nurseTreeData.find(item => item.id === id);
                    if (row?.isMaster) {
                      const childrenIds = nurseTreeData
                        .filter(item => item.parentId === id && !addedNurseRowIds.includes(Number(item.id)))
                        .map(item => item.id);
                      nextSelectedIds = Array.from(new Set([...nextSelectedIds, ...childrenIds]));
                    }
                  });

                  // If a parent was deselected, deselect all its children
                  newlyDeselected.forEach(id => {
                    const row = nurseTreeData.find(item => item.id === id);
                    if (row?.isMaster) {
                      const childrenIds = nurseTreeData.filter(item => item.parentId === id).map(item => item.id);
                      nextSelectedIds = nextSelectedIds.filter(sid => !childrenIds.includes(sid));
                    } else if (row?.parentId) {
                      // If a child was deselected, possibly deselect parent? 
                      // Most tree implementations don't automatically deselect parent unless all children are gone.
                      // But here users might expect it. Let's keep it simple for now.
                    }
                  });

                  setSelectedNurseTreeIds(nextSelectedIds);
                  const selected = nurseRows?.filter(row => nextSelectedIds.includes(row.id));
                  setSelectedNurseRows(selected || []);
                }}
                getRowClassName={(params: any) =>
                  addedNurseRowIds.includes(Number(params.row.id)) ? 'disabled-row' : ''
                }
                isActionColumnShow={false}
                isRowSelectable={(params: any) => !addedNurseRowIds.includes(Number(params.id))}
                treeColumn="name"
              />
            </Box>
          )}
        </Grid>
        <Grid
          item
          xs={12}
          sx={{
            position: isFullscreen ? "absolute" : "sticky",
            top: isFullscreen ? -16 : "auto",
            left: isFullscreen ? -16 : 0,
            width: isFullscreen ? "calc(100% + 32px)" : "100%",
            height: isFullscreen
              ? "calc(100% + 32px)"
              : isArrowUp
                ? 96
                : "400px",
            zIndex: isFullscreen ? 1500 : 1000,
            backgroundColor: "white",
            overflow: "visible", // Changed from auto to visible to avoid double scroll
            transition: "all 0.3s ease",
          }}
        >
          <Card sx={{ height: "100%", display: "flex", flexDirection: "column", boxShadow: "5px -5px 10px 5px rgba(0, 0, 0, 0.25)" }}>
            <Box sx={{ p: 2, borderBottom: "1px solid #E0E0E0", flexShrink: 0 }}>
              <Grid
                container
                alignItems="center"
              >
                <span style={{ fontWeight: "bold", fontSize: "1.2rem" }}>
                  {t("patient-care-history.your-selections")}
                </span>

                <ByzMuiButton
                  sx={{ marginLeft: "auto" }}
                  variant="contained"
                  color="primary"
                  onClick={handleAddPatient}
                  disabled={isAdding}
                >
                  <AddIcon />
                  <ByzMuiTypography fontSize={10}>
                    {isAdding ? "Ekleniyor..." : t("patient-care-guide.add-patient")}
                  </ByzMuiTypography>
                </ByzMuiButton>

                <Box sx={{ ml: 2 }}>
                  <ByzToggleIconButton
                    iconOn="/assets/icons/generals/eye-open.svg"
                    iconOff="/assets/icons/generals/eye-close.svg"
                    altText="eye"
                    value={isEyeOpen}
                    onClick={() => setIsEyeOpen(!isEyeOpen)}
                  />
                  <ByzToggleIconButton
                    iconOn="/assets/icons/generals/full-screen-off.svg"
                    iconOff="/assets/icons/generals/full-screen-on.svg"
                    altText="fullscreen"
                    value={isFullscreen}
                    onClick={() => {
                      const newFullscreen = !isFullscreen;
                      setIsFullscreen(newFullscreen);
                      if (newFullscreen) {
                        setIsArrowUp(false);
                      }
                    }}
                  />
                  <ByzToggleIconButton
                    iconOn="/assets/icons/generals/arrow-up.svg"
                    iconOff="/assets/icons/generals/arrow-down.svg"
                    altText="collapse-expand"
                    value={isArrowUp}
                    disabled={isFullscreen}
                    onClick={() => setIsArrowUp(!isArrowUp)}
                  />
                </Box>
              </Grid>
            </Box>

            {!isArrowUp && (
              <Box sx={{ flexGrow: 1, overflow: "hidden" }}>

                <ByzDataTreeListCarePlan
                  columns={updatedColumnsPlan}
                  initialData={selectedPlanTreeData}
                  hideColumns={false}
                  checkboxSelection
                  sx={{
                    height: "100%",
                    "& .MuiDataGrid-columnHeaderCheckbox .MuiCheckbox-root": {
                      display: "none",
                    },
                    ...commonGridHeaderStyle
                  }}

                  selectedIds={[
                    ...selectedPlanRows.map(row => row.id),
                    ...selectedPlanTreeData.filter(row => row.isMaster).map(row => row.id)
                  ]}
                  onSelectionChange={(ids: any) => {
                    const validIds = (Array.isArray(ids) ? ids : [ids]).map(Number);

                    const checkedParentIds = new Set(
                      selectedPlanTreeData
                        .filter(row => row.isMaster && validIds.includes(Number(row.id)))
                        .map(row => Number(row.id))
                    );

                    setSelectedPlanRows(prev => prev.filter(row => {
                      const treeRow = selectedPlanTreeData.find(tr => tr.id === row.id);
                      const parentIsChecked = treeRow && treeRow.parentId ? checkedParentIds.has(Number(treeRow.parentId)) : true;

                      return validIds.includes(Number(row.id)) && parentIsChecked;
                    }));

                    setSelectedBottomRowIds(validIds);

                    setAddedNurseRowIds(prev => prev.filter(id => validIds.includes(Number(id))));
                  }}
                  isRowSelectable={() => true}
                  showToolbar={isEyeOpen}
                  treeColumn="nursingInitiative"
                  isActionColumnShow={false}
                  defaultExpandAll
                  customToolbar={ByzDataGridToolbar}
                />
              </Box>
            )}
          </Card>
        </Grid>
        {isFullscreen && (
          <Grid item xs={12} sx={{ height: isArrowUp ? 96 : "400px", visibility: "hidden" }} />
        )}
      </Grid>

      {isPlanEditDialogOpen && (
        <Dialog open={isPlanEditDialogOpen} onClose={handleCloseDialogPlanEdit}>
          <PatientCareNursePlanAdd
            pcsid={PlancurrentEditRow}
            setDialogTreeOpen={setPlanEditDialogOpen}
            onTimeSelected={handleTimeSelected}
            selectedPlan={selectedPlanRows.find(row => row.id === PlancurrentEditRow)?.nursingInitiative}
            currentPeriod={selectedPlanRows.find(row => row.id === PlancurrentEditRow)?.period}
          />
        </Dialog>
      )}
    </Box>
  );
}

export default PatientCareGuide;
